import {
  useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef,
  type CSSProperties, type KeyboardEvent, type MouseEvent, type ReactNode, type SyntheticEvent,
} from 'react';
import { Maximize2, Minus, Plus } from 'lucide-react';
import { classifyActivity, type ActiveRunDto, type AgentActivity, type HubPlanetDto } from '@aiai/shared';
import {
  CENTER, ORBIT_TILT, VIEW,
  agentTagForRun, constellationAnchors, isWorkingRun, layoutPlanets, outerOrbit, positionOnOrbit, worldBounds,
  type AgentTag,
} from './layout';
import { PLANET_SCALE, screenFont, type Bounds, type Camera } from './camera';
import type { HubEffect } from './hubLive';
import { Constellation } from './Constellation';
import { litCount } from './constellationGen';
import { newerActivity, type LiveActivity } from './liveActivity';
import { planetNeedsYou } from './needsYou';
import { useSceneClock } from './scene/useSceneClock';
import { usePrefersReducedMotion } from './scene/usePrefersReducedMotion';
import { useElementSize } from './scene/useElementSize';
import { usePanZoom } from './scene/usePanZoom';
import { velocityTier, formatLead } from './velocity';
import { PATCH_GLYPHS, PATCH_NAMES, patchNames, shownPatches } from './patches';

const SWEEP_PERIOD_MS = 7920;
const SWEEP_SLICES = 18;
const COMET_MS = 1400;
const RIPPLE_MS = 700;
const METEOR_MS = 900;
const METEOR_FADE_MS = 300;
const CRASH_MS = 900;
const FLICKER_STEP_MS = 120;
const FLICKER_MS = 600;
const DEBRIS = 5;
/** A long frame (tab hidden, scrolled away) must not teleport the planets. */
const MAX_ORBIT_STEP_MS = 100;
/** Where a planet click flies to. */
const PLANET_VIEW_SCALE = PLANET_SCALE * 1.4;
/** The zoom buttons' step. */
const ZOOM_STEP = 1.75;

export interface UniverseProps {
  planets: HubPlanetDto[];
  activeRuns: ActiveRunDto[];
  effects: HubEffect[];
  /** Newest live tool call per run id (from the /hub socket); wins over a run's snapshot while newer. */
  liveActivity: LiveActivity;
  selectedId: string | null;
  onSelect: (projectId: string | null) => void;
  /** TV mode: a camera cut. Each new `key` flies to `projectId` (null = fit the whole universe). */
  cameraShot?: { projectId: string | null; key: number };
  /** Whether the overview has loaded: only a loaded, empty sky is "No projects yet". */
  status: 'loading' | 'error' | 'ready';
  /** Where the camera starts (a restored view). Read on mount. */
  initialCamera?: Camera | null;
  /** The peek card for the selected planet. */
  renderPeek: (planet: HubPlanetDto, actions: { close: () => void }) => ReactNode;
  /** A constellation star was clicked (or Entered): open that shipped issue. */
  onOpenIssue: (planet: HubPlanetDto, issueId: string) => void;
  /**
   * The camera settled after a gesture or flight (debounced, never per frame). The camera is in
   * the planets' home frame (where they sit at mount), so passing it back as `initialCamera`
   * restores the same view.
   */
  onViewChange: (v: { camera: Camera }) => void;
}

/** The short word under a ship; the full phrase (below) lives in the aria label. */
const ACTIVITY_WORD: Record<AgentActivity, string> = {
  read: 'read', edit: 'edit', test: 'test', ship: 'ship', run: 'run', web: 'web', think: '…',
};
const ENGINEER_DOING: Record<AgentActivity, string> = {
  edit: 'editing code', read: 'reading code', think: 'thinking', run: 'running a command',
  web: 'browsing the web', test: 'running tests', ship: 'shipping',
};
const PM_DOING: Record<AgentActivity, string> = {
  read: 'reading the board', edit: 'writing', web: 'browsing the web',
  test: 'thinking', ship: 'thinking', run: 'thinking', think: 'thinking',
};

const STATUS_TEXT = { loading: 'Loading…', error: "Couldn't load your projects" } as const;

const ease = (k: number) => (k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2);
/**
 * Where the stack of labels under the focused planet starts (world units below its edge): clear
 * of its world-sized rings (selection r + 5, focus r + 7) or 3 px on screen,
 * whichever is further. Other planets draw no rings, so theirs starts 3 px below on screen.
 */
const LABEL_CLEARANCE = 8;
/**
 * A label under a planet, drawn at y = r: `px` tall on screen with its baseline `linePx` screen
 * pixels below the top of the label stack. A CSS px translate on SVG text is in user units, so
 * `--lk` (1 / camera scale) keeps the spacing screen-constant like the font.
 */
const belowPlanet = (px: number, linePx: number, clearRings: boolean): CSSProperties => ({
  ...screenFont(px),
  transform: clearRings
    ? `translateY(calc(max(calc(3px * var(--lk, 1)), ${LABEL_CLEARANCE}px) + ${linePx}px * var(--lk, 1)))`
    : `translateY(calc(${3 + linePx}px * var(--lk, 1)))`,
});
/** Baselines in the stack (screen px from its top): code (12 px caps), then paused, then stats; about 3 px apart. */
const CODE_LINE = 9;
const PAUSED_LINE = 20;
const STATS_LINE = 21;
const STATS_LINE_PAUSED = 33;
/** usePanZoom listens natively on the container, so React's stopPropagation can't shield an overlay from it. */
const shieldPointerDown = (el: HTMLElement | null) => {
  el?.addEventListener('pointerdown', (e) => e.stopPropagation());
};
/** Overlays (controls, peek card) keep double-clicks to themselves instead of zooming the canvas. */
const keepDoubleClick = (e: SyntheticEvent) => e.stopPropagation();

/** One slice of the radar wedge; `r` is the outermost orbit, so the sweep reaches the last circle. */
function sweepSlice(i: number, r: number): string {
  const a0 = (-i * 2.5 * Math.PI) / 180;
  const a1 = (-(i + 1) * 2.5 * Math.PI) / 180;
  return `M0,0 L${r * Math.cos(a0)},${r * Math.sin(a0)} A${r},${r} 0 0 0 ${r * Math.cos(a1)},${r * Math.sin(a1)} Z`;
}

function planetLabel(p: HubPlanetDto, tag: AgentTag | undefined, activityWords: string | null): string {
  const parts = [
    `${p.code ?? p.name} ${p.kind === 'prototype' ? 'prototype' : 'project'}`,
    `${p.openIssueCount} open issue${p.openIssueCount === 1 ? '' : 's'}`,
  ];
  if (tag) {
    parts.push(tag === 'E' ? 'Engineer working' : 'PM working');
    if (activityWords) parts.push(activityWords);
  }
  const tier = velocityTier(p.velocity);
  if (tier && p.velocity?.leadMinutesMedian7d != null) parts.push(`ships in ${formatLead(p.velocity.leadMinutesMedian7d)}`);
  const names = patchNames(p);
  if (names.length) parts.push(`patches: ${names.join(', ')}`);
  if (p.autonomy === 'manual') parts.push('paused');
  if (planetNeedsYou(p)) parts.push('needs you');
  return parts.join(', ');
}

function statsLine(p: HubPlanetDto, tag: AgentTag | undefined): string {
  const working = tag ? ` · ${tag === 'E' ? 'Engineer' : 'PM'} working` : '';
  return `${p.openIssueCount} open · ${p.shippedCount} shipped${working}`;
}

/** A wisp of smoke over a planet with failed issues: puff `i` at clock `t` (ms), for planet radius `r`. */
function smokePuff(i: number, t: number, r: number) {
  const ph = (t / 2400 + i / 3) % 1;
  return { cx: 1.5 * Math.sin(ph * 6), cy: -r - 3 - ph * 10, r: 1 + ph * 2.2, opacity: 0.55 * (1 - ph) };
}

export function Universe({
  planets, activeRuns, effects, liveActivity, selectedId, onSelect, cameraShot, status, renderPeek,
  onOpenIssue, onViewChange, initialCamera,
}: UniverseProps) {
  const reduced = usePrefersReducedMotion();
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const worldRef = useRef<SVGGElement>(null);
  const sweepRef = useRef<SVGGElement>(null);
  const planetEls = useRef(new Map<string, SVGGElement>());
  const haloEls = useRef(new Map<string, SVGCircleElement>());
  const rippleEls = useRef(new Map<string, SVGCircleElement>());
  const shipEls = useRef(new Map<string, SVGGElement>());
  const smokeEls = useRef(new Map<string, SVGGElement>());
  const cometEls = useRef(new Map<string, SVGCircleElement>());
  const fxEls = useRef(new Map<string, SVGElement>());
  const starRingEls = useRef(new Map<string, SVGCircleElement>());
  const positions = useRef(new Map<string, { x: number; y: number }>());
  /** Orbit time: advances by the (capped) frame delta. */
  const orbitT = useRef(0);
  const lastT = useRef<number | null>(null);

  const slots = useMemo(() => layoutPlanets(planets), [planets]);
  // Keyed by value: a poll that returns the same sky must not hand usePanZoom new bounds
  // (that would refit, and re-bind its listeners).
  const boundsKey = JSON.stringify(worldBounds(slots));
  const bounds = useMemo<Bounds>(() => JSON.parse(boundsKey) as Bounds, [boundsKey]);
  const vp = useElementSize(containerRef, VIEW);

  // Engineer wins when a project has both a build and a plan run. Queued and
  // gated runs get no ship: motion only for an agent actually working.
  const { tagByProject, runByProject } = useMemo(() => {
    const m = new Map<string, AgentTag>();
    const runs = new Map<string, ActiveRunDto>();
    for (const r of activeRuns) {
      if (!isWorkingRun(r)) continue;
      if (m.get(r.projectId) !== 'E') {
        m.set(r.projectId, agentTagForRun(r));
        runs.set(r.projectId, r);
      }
    }
    return { tagByProject: m, runByProject: runs };
  }, [activeRuns]);

  // What the ship's agent is doing: the newer of the snapshot and the live socket, classified now.
  const activityFor = (projectId: string): { activity: AgentActivity; words: string } | null => {
    const r = runByProject.get(projectId);
    const tag = tagByProject.get(projectId);
    if (!r || !tag) return null;
    const activity = classifyActivity(newerActivity(r.activity, liveActivity[r.id]), Date.now());
    return { activity, words: (tag === 'E' ? ENGINEER_DOING : PM_DOING)[activity] };
  };

  const initial = useMemo(
    () => new Map(slots.map((s) => [s.planet.id, positionOnOrbit(s.orbit, s.phase)])),
    [slots],
  );
  const posOf = (id: string) => positions.current.get(id) ?? initial.get(id);

  const settledRef = useRef(() => {});
  // `initial` is only read on mount (usePanZoom keeps its own camera afterwards).
  const { level, active, hint, getCamera, flyTo, follow, zoomBy, fit } = usePanZoom({
    containerRef, worldRef, viewport: vp, bounds, initial: initialCamera, reducedMotion: reduced,
    onSettle: () => settledRef.current(),
  });

  // Focus: the selected planet, else (zoomed in) the planet nearest the camera centre.
  const nearestId = useCallback((): string | null => {
    const cam = getCamera();
    let best: string | null = null;
    let bestD = Infinity;
    for (const s of slots) {
      const p = positions.current.get(s.planet.id) ?? initial.get(s.planet.id);
      if (!p) continue;
      const d = Math.hypot(p.x - cam.x, p.y - cam.y);
      if (d < bestD) {
        bestD = d;
        best = s.planet.id;
      }
    }
    return best;
  }, [getCamera, slots, initial]);
  const zoomedIn = level !== 'system';

  const focusId = selectedId ?? (zoomedIn ? nearestId() : null);

  // Report a settled camera. It rides along with the focused planet as it orbits, so it is
  // reported shifted back to that planet's home: a remount starts every planet at home, and
  // the same camera then shows the same view.
  const onViewChangeRef = useRef(onViewChange);
  onViewChangeRef.current = onViewChange;
  const focusNow = useRef(focusId);
  focusNow.current = focusId;
  settledRef.current = () => {
    const cam = getCamera();
    const id = focusNow.current;
    const pos = id ? positions.current.get(id) : undefined;
    const home = id ? initial.get(id) : undefined;
    const camera = pos && home ? { x: cam.x + home.x - pos.x, y: cam.y + home.y - pos.y, scale: cam.scale } : cam;
    onViewChangeRef.current({ camera });
  };

  // The camera moves without re-rendering; re-render when the nearest planet changes under it.
  const [, rerender] = useReducer((n: number) => n + 1, 0);
  const shownNearest = useRef<string | null>(null);
  const refreshFocus = useRef(() => {});
  useLayoutEffect(() => {
    shownNearest.current = zoomedIn && !selectedId ? focusId : null;
    refreshFocus.current = () => {
      if (!zoomedIn || selectedId) return;
      if (nearestId() !== shownNearest.current) rerender();
    };
  });
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    // After the event's own handlers (and any immediate camera move) have run.
    const later = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => refreshFocus.current(), 0);
    };
    const kinds = ['pointerup', 'wheel', 'keyup', 'click'] as const;
    for (const k of kinds) el.addEventListener(k, later);
    return () => {
      for (const k of kinds) el.removeEventListener(k, later);
      if (timer) clearTimeout(timer);
    };
  }, []);

  useSceneClock(
    svgRef,
    (t) => {
      const dt = lastT.current === null ? 0 : Math.min(Math.max(t - lastT.current, 0), MAX_ORBIT_STEP_MS);
      lastT.current = t;
      // Orbits always turn; the camera rides along with the focused planet (selected, or nearest
      // while zoomed in), so it stays put on screen while the sky turns around it.
      orbitT.current += dt;
      const ot = orbitT.current;
      const deg = ((t / SWEEP_PERIOD_MS) * 360) % 360;
      sweepRef.current?.setAttribute('transform', `translate(${CENTER.x},${CENTER.y}) scale(1,${ORBIT_TILT}) rotate(${deg})`);
      for (const s of slots) {
        const id = s.planet.id;
        // Paused (manual) planets hold still; comets still need their position.
        const paused = s.planet.autonomy === 'manual';
        const pos = positionOnOrbit(s.orbit, paused ? s.phase : s.phase + ot * s.speed);
        if (id === focusId) {
          const was = posOf(id)!;
          follow(pos.x - was.x, pos.y - was.y);
        }
        positions.current.set(id, pos);
        planetEls.current.get(id)?.setAttribute('transform', `translate(${pos.x},${pos.y})`);
        const angle = ((Math.atan2((pos.y - CENTER.y) / ORBIT_TILT, pos.x - CENTER.x) * 180) / Math.PI + 360) % 360;
        const behind = (deg - angle + 360) % 360;
        const k = !paused && behind < 90 ? 1 - behind / 90 : 0;
        haloEls.current.get(id)?.setAttribute('opacity', String((tagByProject.has(id) ? 0.3 : 0.1) * k));
        const ship = shipEls.current.get(id);
        if (ship) {
          const a = t / 700 + s.phase;
          ship.setAttribute('transform', `translate(${(s.radius + 11) * Math.cos(a)},${(s.radius + 11) * 0.6 * Math.sin(a)})`);
        }
        const smoke = smokeEls.current.get(id);
        if (smoke && !paused) {
          smoke.childNodes.forEach((puff, i) => {
            const q = smokePuff(i, t, s.radius);
            const el = puff as SVGCircleElement;
            el.setAttribute('cx', String(q.cx));
            el.setAttribute('cy', String(q.cy));
            el.setAttribute('r', String(q.r));
            el.setAttribute('opacity', String(q.opacity));
          });
        }
      }
      for (const e of effects) {
        if (e.kind === 'meteor' || e.kind === 'crash' || e.kind === 'flicker') {
          const fx = fxEls.current.get(e.id);
          const at = positions.current.get(e.projectId);
          if (!fx || !at) continue;
          const el = t - e.at;
          if (e.kind === 'flicker') {
            fx.setAttribute('cx', String(at.x));
            fx.setAttribute('cy', String(at.y));
            fx.setAttribute('opacity', el < 0 || el >= FLICKER_MS ? '0' : Math.floor(el / FLICKER_STEP_MS) % 2 === 0 ? '0.35' : '1');
          } else if (e.kind === 'meteor') {
            const k = Math.min(1, Math.max(0, el / METEOR_MS));
            const off = -60 * (1 - k);
            fx.setAttribute('transform', `translate(${at.x + off},${at.y + off})`);
            fx.setAttribute('opacity', el < 0 || el >= METEOR_MS + METEOR_FADE_MS ? '0' : String(el < METEOR_MS ? 1 : 1 - (el - METEOR_MS) / METEOR_FADE_MS));
          } else {
            const k = Math.min(1, Math.max(0, el / CRASH_MS));
            const pr = Number(fx.getAttribute('data-radius'));
            fx.setAttribute('transform', `translate(${at.x},${at.y})`);
            fx.setAttribute('opacity', el < 0 || k >= 1 ? '0' : String(1 - k));
            fx.childNodes.forEach((n, i) => {
              const c = n as SVGCircleElement;
              if (i === 0) {
                c.setAttribute('r', String(pr + 14 * k));
              } else {
                const a = ((i - 1) / DEBRIS) * Math.PI * 2;
                c.setAttribute('cx', String(Math.cos(a) * (pr + 18 * k)));
                c.setAttribute('cy', String(Math.sin(a) * (pr + 18 * k)));
              }
            });
          }
          continue;
        }
        const comet = cometEls.current.get(e.id);
        const target = positions.current.get(e.projectId);
        if (!comet || !target) continue;
        const k = Math.min(1, Math.max(0, (t - e.at) / COMET_MS));
        const q = ease(k);
        const [ax, ay, bx, by] =
          e.kind === 'comet-out' ? [CENTER.x, CENTER.y, target.x, target.y] : [target.x, target.y, CENTER.x, CENTER.y];
        const mx = (ax + bx) / 2;
        const my = (ay + by) / 2 - 30;
        comet.setAttribute('cx', String((1 - q) * (1 - q) * ax + 2 * (1 - q) * q * mx + q * q * bx));
        comet.setAttribute('cy', String((1 - q) * (1 - q) * ay + 2 * (1 - q) * q * my + q * q * by));
        comet.setAttribute('opacity', t < e.at || k >= 1 ? '0' : '1');
        const r = (t - e.at - COMET_MS) / RIPPLE_MS;
        const ring = e.kind === 'comet-out' ? rippleEls.current.get(e.projectId) : starRingEls.current.get(e.projectId);
        if (ring && r >= 0 && r < 1) {
          ring.setAttribute('opacity', String(1 - r));
          ring.setAttribute('stroke-width', String(1.5 + 2 * (1 - r)));
        } else if (ring && r >= 1) {
          ring.setAttribute('opacity', '0');
        }
      }
      refreshFocus.current();
    },
    !reduced,
  );

  const flyToPlanet = (id: string, scale: number) => {
    const p = posOf(id);
    if (p) flyTo({ x: p.x, y: p.y, scale });
  };
  // TV director: each new `key` is a cut. Flying to a planet or back to the fit
  // view; a shot at a planet that is gone is ignored. The key present at mount is not a cut.
  const lastShotKey = useRef(cameraShot?.key);
  const cutRef = useRef<() => void>(() => {});
  cutRef.current = () => {
    if (!cameraShot) return;
    if (selectedId) onSelect(null);
    if (cameraShot.projectId === null) fit();
    else flyToPlanet(cameraShot.projectId, PLANET_VIEW_SCALE);
  };
  const shotKey = cameraShot?.key;
  useEffect(() => {
    if (shotKey === lastShotKey.current) return;
    lastShotKey.current = shotKey;
    cutRef.current();
  }, [shotKey]);
  const select = (id: string) => {
    if (selectedId === id) return onSelect(null);
    onSelect(id);
    flyToPlanet(id, PLANET_VIEW_SCALE);
  };
  const onPlanetKey = (id: string) => (e: KeyboardEvent<SVGGElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      select(id);
    }
  };
  const onPlanetClick = (id: string) => (e: MouseEvent<SVGGElement>) => {
    e.stopPropagation();
    // The second click of a double-click is not a toggle.
    if (e.detail > 1) return;
    select(id);
  };
  // A double-click on a planet only selects it. Capture phase:
  // React delegates at the root, so a bubble-phase handler would run after the container's
  // native dblclick zoom; preventDefault() tells that zoom to stand down.
  const onPlanetDoubleClick = (e: MouseEvent<SVGGElement>) => e.preventDefault();
  const setRef = <T extends Element>(map: Map<string, T>, id: string) => (el: T | null) => {
    if (el) map.set(id, el);
    else map.delete(id);
  };

  const outer = outerOrbit(slots);
  // Only planets that draw a constellation take a place on the ring, so the gaps stay even.
  const anchors = useMemo(() => constellationAnchors(
    slots.filter((s) => litCount(s.planet.shippedCount) > 0).map((s) => s.planet.id), outer,
  ), [slots, outer]);
  const selected = slots.find((s) => s.planet.id === selectedId)?.planet ?? null;
  return (
    <div
      ref={containerRef}
      tabIndex={0}
      role="group"
      aria-label="Agent hub universe. Use plus and minus to zoom, arrow keys to pan, 0 to fit."
      // Until the canvas is tapped, a vertical swipe scrolls the page instead of panning.
      className={`relative h-full w-full bg-surface-2 select-none outline-none focus-visible:ring-2 focus-visible:ring-signal/60 ${
        active ? 'touch-none' : 'touch-pan-y'}`}
    >
      <svg
        ref={svgRef}
        width={vp.width}
        height={vp.height}
        viewBox={`0 0 ${vp.width} ${vp.height}`}
        className="absolute inset-0 block h-full w-full"
        onClick={() => onSelect(null)}
      >
        {/* The camera: usePanZoom owns this group's transform and --lk; React never sets either. */}
        <g ref={worldRef}>
          {/* Constellations: one generated figure per planet with shipped work. */}
          {slots.filter((s) => anchors.has(s.planet.id)).map((s) => (
            <Constellation
              key={`c-${s.planet.id}`}
              planet={s.planet}
              anchor={anchors.get(s.planet.id)!}
              // Stars and captions only once zoomed in past fit; at fit, hover or focus reveals them.
              detailed={zoomedIn}
              onOpenIssue={onOpenIssue}
              headRef={setRef(starRingEls.current, s.planet.id)}
            />
          ))}

          <g aria-hidden="true">
            {slots.map((s) => (
              <ellipse key={`o-${s.planet.id}`} cx={CENTER.x} cy={CENTER.y} rx={s.orbit} ry={s.orbit * ORBIT_TILT}
                fill="none" className="stroke-line" />
            ))}
            {status === 'ready' && slots.length === 0 && (
              <ellipse cx={CENTER.x} cy={CENTER.y} rx={120} ry={60} fill="none" className="stroke-line-strong" strokeDasharray="4 6" />
            )}
          </g>

          <g ref={sweepRef} data-testid="sweep" aria-hidden="true" transform={`translate(${CENTER.x},${CENTER.y}) scale(1,${ORBIT_TILT})`}>
            {Array.from({ length: SWEEP_SLICES }, (_, i) => (
              <path key={i} d={sweepSlice(i, outer)} className="fill-signal" opacity={0.1 * (1 - i / SWEEP_SLICES)} />
            ))}
          </g>

          {/* The sun's glyphs scale with the sun itself (world geometry, not a label). */}
          <g aria-hidden="true">
            {Array.from({ length: 24 }, (_, i) => {
              const a = (i * 15 * Math.PI) / 180;
              const r2 = i % 2 ? 42 : 47;
              return (
                <line key={i} x1={CENTER.x + 36 * Math.cos(a)} y1={CENTER.y + 36 * Math.sin(a)}
                  x2={CENTER.x + r2 * Math.cos(a)} y2={CENTER.y + r2 * Math.sin(a)}
                  className="stroke-ink" strokeWidth={1.2} strokeLinecap="round" />
              );
            })}
            <circle cx={CENTER.x} cy={CENTER.y} r={28} className="fill-ink" />
            <text x={CENTER.x} y={CENTER.y + 4} textAnchor="middle"
              className="fill-surface-1 font-display text-[11px] font-bold uppercase tracking-[0.08em]">aiai</text>
          </g>
          {status !== 'ready' ? (
            <text x={CENTER.x} y={CENTER.y + 92} textAnchor="middle" className="fill-ink-muted" style={screenFont(12)}>
              {STATUS_TEXT[status]}
            </text>
          ) : slots.length === 0 && (
            <text x={CENTER.x} y={CENTER.y + 92} textAnchor="middle" className="fill-ink-muted" style={screenFont(12)}>
              No projects yet
            </text>
          )}

          <g aria-hidden="true">
            {effects.filter((e) => e.kind === 'comet-out' || e.kind === 'comet-home').map((e) => {
              return (
              <circle key={e.id} data-testid={`comet-${e.kind}`} ref={setRef(cometEls.current, e.id)} r={e.kind === 'comet-home' ? 3.5 : 2.6}
                className={e.kind === 'comet-home' ? 'fill-ok' : 'fill-signal'} opacity={0} />
              );
            })}
          </g>

          {slots.map((s) => {
            const p = s.planet;
            const tag = tagByProject.get(p.id);
            const act = activityFor(p.id);
            const isSelected = selectedId === p.id;
            const paused = p.autonomy === 'manual';
            const pos = posOf(p.id)!;
            const r = s.radius;
            const shown = shownPatches(p);
            const detailed = zoomedIn && focusId === p.id;
            // Only the focused planet draws rings (selection, focus) its labels must clear.
            const focused = focusId === p.id || isSelected;
            const tier = velocityTier(p.velocity);
            return (
              <g key={p.id} ref={setRef(planetEls.current, p.id)} transform={`translate(${pos.x},${pos.y})`}
                role="button" tabIndex={0}
                aria-label={planetLabel(p, tag, act?.words ?? null)} aria-pressed={isSelected}
                className="group cursor-pointer outline-none"
                onClick={onPlanetClick(p.id)} onKeyDown={onPlanetKey(p.id)} onDoubleClickCapture={onPlanetDoubleClick}>
                <circle ref={setRef(haloEls.current, p.id)} r={r + 6} className={tag ? 'fill-signal' : 'fill-ink-faint'} opacity={0} />
                <circle ref={setRef(rippleEls.current, p.id)} r={r + 4} fill="none" className="stroke-signal" opacity={0} />
                <circle r={r + 7} fill="none" className="stroke-ink opacity-0 group-focus-visible:opacity-100" strokeWidth={2} />
                {isSelected && <circle r={r + 5} fill="none" className="stroke-ink" strokeWidth={1.5} strokeDasharray="3 3" />}
                {/* Only the planet itself dims when paused; needs-you, focus and a ship stay at full strength. */}
                <g className={paused ? 'opacity-50' : undefined}>
                  {p.kind === 'prototype' && (
                    <ellipse rx={r * 1.75} ry={r * 0.5} fill="none" className="stroke-ink" strokeWidth={1.1} transform="rotate(-18)" />
                  )}
                  {tier && (
                    <circle data-testid="velocity-halo" data-tier={tier} r={r + 5} fill="none"
                      className={tier === 'fast' ? 'stroke-ink' : 'stroke-line-strong'}
                      strokeWidth={1.4} opacity={tier === 'fast' ? 0.55 : 0.5} />
                  )}
                  <circle r={r} className={`fill-surface-1 ${tag ? 'stroke-ink' : 'stroke-line-strong'}`} strokeWidth={tag ? 1.6 : 1.1} />
                  <path d={`M${-r * 0.8},${-r * 0.25} Q0,${-r * 0.45} ${r * 0.8},${-r * 0.25}`} fill="none" className="stroke-line" />
                  <path d={`M${-r * 0.85},${r * 0.2} Q0,${r * 0.38} ${r * 0.85},${r * 0.2}`} fill="none" className="stroke-line" />
                  <text y={r} textAnchor="middle" style={belowPlanet(12, CODE_LINE, focused)}
                    className={`font-display font-bold uppercase tracking-[0.08em] ${tag ? 'fill-ink' : 'fill-ink-faint'}`}>
                    {p.code ?? p.name.slice(0, 10)}
                  </text>
                  {paused && (
                    <text y={r} textAnchor="middle" className="fill-ink-faint font-mono" style={belowPlanet(10, PAUSED_LINE, focused)}>paused</text>
                  )}
                </g>
                {/* The peek card already says it for the selected planet. */}
                {detailed && !isSelected && (
                  <text y={r} textAnchor="middle" aria-hidden="true" className="fill-ink-muted font-mono"
                    style={belowPlanet(11, paused ? STATS_LINE_PAUSED : STATS_LINE, focused)}>
                    {statsLine(p, tag)}
                  </text>
                )}
                {zoomedIn && shown.length > 0 && (
                  <g data-testid="patches" aria-hidden="true" transform={`translate(0,${r + (paused ? 40 : 32)})`}>
                    {shown.map((k, i, all) => (
                      <g key={k} transform={`translate(${(i - (all.length - 1) / 2) * 13},0)`}>
                        <title>{PATCH_NAMES[k]}</title>
                        <circle r={5} className="fill-surface-1 stroke-ink" strokeWidth={0.8} />
                        <text textAnchor="middle" dominantBaseline="central" className="fill-ink font-mono" style={screenFont(5)}>
                          {PATCH_GLYPHS[k]}
                        </text>
                      </g>
                    ))}
                  </g>
                )}
                {zoomedIn && p.failedIssueCount > 0 && (
                  <g ref={setRef(smokeEls.current, p.id)} data-testid="smoke" aria-hidden="true">
                    {[0, 1, 2].map((i) => {
                      const q = smokePuff(i, 0, r);
                      return <circle key={i} cx={q.cx} cy={q.cy} r={q.r} opacity={q.opacity} className="fill-line-strong" />;
                    })}
                  </g>
                )}
                {planetNeedsYou(p) && (
                  <circle cx={r * 0.75} cy={-r * 0.75} r={3.8} className="fill-signal stroke-surface-1" strokeWidth={1.2} />
                )}
                {/* The ship's letter is part of the ship: it scales with it. The outer group keeps the
                    per-frame translate attribute (a CSS transform would override it); the inner one
                    stops growing past 3× so a ship never renders huge while zooming in. */}
                {tag && (
                  <g ref={setRef(shipEls.current, p.id)} transform={`translate(${r + 11},0)`}>
                    <g data-testid="ship" style={{ transform: 'scale(min(1, calc(var(--lk) * 3)))' }}>
                      <circle r={5.5} className={tag === 'E' ? 'fill-ink stroke-ink' : 'fill-surface-1 stroke-ink'} strokeWidth={1.1} />
                      <text y={2.2} textAnchor="middle"
                        className={`font-display font-bold ${tag === 'E' ? 'fill-surface-1 text-[5.5px]' : 'fill-ink text-[4.2px]'}`}>
                        {tag}
                      </text>
                      {detailed && act && (
                        // 10 px on screen: the ship's own scale (min(1, 3 * --lk)) is divided back out.
                        <text data-testid="ship-activity" y={11} textAnchor="middle" className="fill-ink-muted font-mono"
                          style={{ fontSize: 'calc(10px * var(--lk, 1) / min(1, calc(var(--lk, 1) * 3)))' }}>
                          {ACTIVITY_WORD[act.activity]}
                        </text>
                      )}
                    </g>
                  </g>
                )}
              </g>
            );
          })}

          {/* Above the planet bodies (an overlay under an opaque planet would be invisible). */}
          <g aria-hidden="true" pointerEvents="none" data-testid="effects-layer">
            {effects.map((e) => {
              if (e.kind === 'meteor') {
                return (
                  <g key={e.id} data-testid="effect-meteor" ref={setRef(fxEls.current, e.id)} opacity={0}>
                    <line x1={-14} y1={-16} x2={0} y2={0} className="stroke-signal" strokeWidth={1.6} strokeLinecap="round" />
                  </g>
                );
              }
              if (e.kind === 'crash') {
                const pr = slots.find((s) => s.planet.id === e.projectId)?.radius ?? 0;
                return (
                  <g key={e.id} data-testid="effect-crash" data-radius={pr} ref={setRef(fxEls.current, e.id)} opacity={0}>
                    <circle data-crash-ring r={pr} fill="none" className="stroke-bad" strokeWidth={1.5} />
                    {Array.from({ length: DEBRIS }, (_, i) => (
                      <circle key={i} data-crash-debris r={1.4} className="fill-bad" />
                    ))}
                  </g>
                );
              }
              if (e.kind === 'flicker') {
                const pr = slots.find((s) => s.planet.id === e.projectId)?.radius ?? 0;
                return <circle key={e.id} data-testid="effect-flicker" ref={setRef(fxEls.current, e.id)} r={pr} className="fill-bad" opacity={0} />;
              }
              return null;
            })}
          </g>
        </g>
      </svg>

      {hint && (
        <div className="pointer-events-none absolute left-1/2 top-3 z-10 -translate-x-1/2 rounded-full bg-ink px-3 py-1 text-xs text-surface-1">
          Ctrl + scroll to zoom
        </div>
      )}

      {selected && (
        <div ref={shieldPointerDown} className="absolute bottom-3 left-3 z-10 max-w-[calc(100%-4.5rem)] select-text"
          onDoubleClickCapture={keepDoubleClick}>
          {renderPeek(selected, { close: () => onSelect(null) })}
        </div>
      )}

      {/* A row on phones, where a short canvas's column would cover the scene. */}
      <div ref={shieldPointerDown} className="absolute bottom-3 right-3 z-10 flex gap-1 sm:flex-col" onDoubleClickCapture={keepDoubleClick}>
        <button type="button" className="btn-secondary btn-sm bg-surface-1 shadow-sm" aria-label="Zoom in" title="Zoom in"
          onClick={() => zoomBy(ZOOM_STEP)}>
          <Plus className="h-4 w-4" aria-hidden="true" />
        </button>
        <button type="button" className="btn-secondary btn-sm bg-surface-1 shadow-sm" aria-label="Zoom out" title="Zoom out"
          onClick={() => zoomBy(1 / ZOOM_STEP)}>
          <Minus className="h-4 w-4" aria-hidden="true" />
        </button>
        <button type="button" className="btn-secondary btn-sm bg-surface-1 shadow-sm" aria-label="Fit all" title="Fit all"
          onClick={() => {
            fit();
            onSelect(null);
          }}>
          <Maximize2 className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
